import React, { useRef, useState, useEffect, useCallback } from "react";
import { Users, Download, Link as LinkIcon, Share2, Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const truncate = (s, n) => (s && s.length > n ? s.slice(0, n - 1) + "…" : s || "");
const scoreColor = (n) => (n >= 80 ? "#10b981" : n >= 60 ? "#f59e0b" : "#f43f5e");

// Shareable Playtest Report — a squad-feedback + fixes summary drawn to a 1200×630 canvas card,
// with the full per-system breakdown below. Aggregates every QD / Playtester-Army review.
export default function PlaytestReport({ gameId, title, genre }) {
  const canvasRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/compiler/${gameId}/playtest-report`)
      .then(({ data }) => setReport(data))
      .catch(() => toast.error("Couldn't load the playtest report."))
      .finally(() => setLoading(false));
  }, [open, gameId]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !report) return;
    const ctx = canvas.getContext("2d");
    const W = 1200, H = 630;
    const s = report.summary || {};

    const grad = ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, "#0b0616"); grad.addColorStop(0.55, "#1a0f2e"); grad.addColorStop(1, "#a855f7");
    ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "rgba(6,4,12,0.6)"; ctx.fillRect(0, 0, W, H);

    ctx.textBaseline = "top"; ctx.textAlign = "left";
    ctx.fillStyle = "rgba(255,255,255,0.85)"; ctx.font = "bold 30px Inter, system-ui, sans-serif";
    ctx.fillText(`${s.squad ? "🎮 AI PLAYTEST SQUAD" : "🛡️ QUALITY DIRECTOR"} · REPORT`, 60, 48);

    ctx.fillStyle = "rgba(255,255,255,0.97)"; ctx.font = "bold 58px Inter, system-ui, sans-serif";
    ctx.fillText(truncate(title || "My Game", 30), 58, 104);
    if (genre) { ctx.fillStyle = "rgba(255,255,255,0.6)"; ctx.font = "26px Inter, system-ui, sans-serif"; ctx.fillText(truncate(genre, 46), 60, 178); }

    // Big average score
    const avg = s.avg_score;
    ctx.save();
    ctx.shadowColor = avg != null ? scoreColor(avg) : "#a855f7"; ctx.shadowBlur = 24;
    ctx.fillStyle = avg != null ? scoreColor(avg) : "#c4b5fd"; ctx.font = "bold 168px Inter, system-ui, sans-serif";
    ctx.fillText(avg != null ? `${avg}` : "—", 56, 236);
    ctx.restore();
    ctx.fillStyle = "rgba(255,255,255,0.85)"; ctx.font = "30px Inter, system-ui, sans-serif";
    ctx.fillText("avg quality score", 60, 432);

    // Stat pills on the right
    const stats = [
      [`${s.systems_reviewed || 0}`, "systems reviewed"],
      [`${s.reviews || 0}`, "playtests run"],
      [`${s.fixes || 0}`, "auto-fixes applied"],
    ];
    let sy = 250;
    for (const [big, small] of stats) {
      ctx.textAlign = "right";
      ctx.fillStyle = "#e9d5ff"; ctx.font = "bold 60px Inter, system-ui, sans-serif";
      ctx.fillText(big, 1140, sy);
      ctx.fillStyle = "rgba(255,255,255,0.6)"; ctx.font = "24px Inter, system-ui, sans-serif";
      ctx.fillText(small, 1140, sy + 62);
      sy += 118;
    }
    ctx.textAlign = "left";
    ctx.fillStyle = "rgba(255,255,255,0.75)"; ctx.font = "24px Inter, system-ui, sans-serif";
    ctx.fillText(`▶ Play & remix it — ${truncate(`${window.location.host}/app/arcade/${gameId}`, 46)}`, 60, 584);
  }, [report, title, genre, gameId]);

  useEffect(() => { if (open && report) draw(); }, [open, report, draw]);

  const shareUrl = `${window.location.origin}/app/arcade/${gameId}`;
  const toBlob = () => new Promise((res) => canvasRef.current.toBlob(res, "image/png"));
  const download = async () => {
    const blob = await toBlob(); if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `playtest-report.png`; a.click();
    URL.revokeObjectURL(url); toast.success("Playtest report downloaded!");
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Share link copied!"); }
    catch { toast.error("Couldn't copy the link."); }
  };
  const nativeShare = async () => {
    const s = report?.summary || {};
    const text = `"${title || "My game"}" scored ${s.avg_score ?? "—"} avg across ${s.systems_reviewed ?? 0} systems in the GameView AI Playtest — come play it!`;
    try {
      const blob = await toBlob();
      const file = blob ? new File([blob], "gameview-playtest.png", { type: "image/png" }) : null;
      if (file && typeof navigator.canShare === "function" && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "GameView", text, url: shareUrl });
      } else { await navigator.share({ title: "GameView", text, url: shareUrl }); }
    } catch { /* cancelled */ }
  };

  const systems = report?.systems || [];
  const findings = report?.findings || [];
  const trend = report?.trend || [];
  const vtrend = report?.visual_trend || [];
  const hasData = (report?.summary?.reviews || 0) > 0;
  const trendDelta = trend.length >= 2 ? Math.round(trend[trend.length - 1].avg - trend[0].avg) : 0;
  const trendPts = trend.map((p, i) => {
    const x = trend.length > 1 ? (i / (trend.length - 1)) * 296 + 2 : 150;
    const y = 60 - (Math.max(0, Math.min(100, p.avg)) / 100) * 54;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const vtrendDelta = vtrend.length >= 2 ? Math.round(vtrend[vtrend.length - 1].score - vtrend[0].score) : 0;
  const vtrendPts = vtrend.map((p, i) => {
    const x = vtrend.length > 1 ? (i / (vtrend.length - 1)) * 296 + 2 : 150;
    const y = 60 - (Math.max(0, Math.min(100, p.score)) / 100) * 54;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full gap-1.5 h-7 px-3 text-xs" data-testid="compiler-report-btn">
          <Users className="w-3.5 h-3.5" /> Playtest report
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[88vh] overflow-y-auto" data-testid="compiler-report-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#a855f7]" /> AI Playtest Report</DialogTitle></DialogHeader>

        {loading ? (
          <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--muted-foreground))]" /></div>
        ) : !hasData ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))] leading-snug py-4" data-testid="compiler-report-empty">
            No playtests yet. Run <b>Complete the Vision</b> with the <b>Quality Director</b> or <b>Playtester Army</b> turned on, then come back — every system the squad reviews (and every fix it makes) will appear here as a shareable report.
          </p>
        ) : (
          <>
            <canvas ref={canvasRef} width={1200} height={630} className="w-full rounded-xl border border-[hsl(var(--border))]" data-testid="compiler-report-card" />
            <div className="flex flex-wrap gap-2">
              <Button onClick={download} className="rounded-full gap-1.5 flex-1 bg-gradient-to-r from-[#7c3aed] to-[#a855f7]" data-testid="compiler-report-download"><Download className="w-4 h-4" /> Image</Button>
              <Button variant="outline" onClick={copyLink} className="rounded-full gap-1.5 flex-1" data-testid="compiler-report-copy"><LinkIcon className="w-4 h-4" /> Copy link</Button>
              {typeof navigator !== "undefined" && navigator.share && (
                <Button variant="outline" onClick={nativeShare} className="rounded-full gap-1.5 flex-1" data-testid="compiler-report-share"><Share2 className="w-4 h-4" /> Share…</Button>
              )}
            </div>

            {/* Quality trend over time (Report Trends) */}
            {trend.length >= 2 && (
              <div className="mt-1" data-testid="compiler-report-trend">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Quality trend</div>
                  <div className="text-[11px] font-semibold" style={{ color: trendDelta >= 0 ? "#10b981" : "#f43f5e" }} data-testid="compiler-report-trend-delta">
                    {trendDelta >= 0 ? `▲ +${trendDelta}` : `▼ ${trendDelta}`} over {trend.length} runs
                  </div>
                </div>
                <svg viewBox="0 0 300 64" className="w-full h-16 mt-1 rounded-lg bg-[hsl(var(--muted))]/30" preserveAspectRatio="none">
                  <line x1="0" y1="33" x2="300" y2="33" stroke="hsl(var(--border))" strokeWidth="0.5" strokeDasharray="3 3" />
                  <polyline points={trendPts} fill="none" stroke="url(#tg)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                  {trend.map((p, i) => {
                    const x = trend.length > 1 ? (i / (trend.length - 1)) * 296 + 2 : 150;
                    const y = 60 - (Math.max(0, Math.min(100, p.avg)) / 100) * 54;
                    return <circle key={i} cx={x} cy={y} r={i === trend.length - 1 ? 3.2 : 2} fill={p.avg >= 80 ? "#10b981" : p.avg >= 60 ? "#f59e0b" : "#f43f5e"} />;
                  })}
                  <defs><linearGradient id="tg" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#0ea5e9" /><stop offset="1" stopColor="#a855f7" /></linearGradient></defs>
                </svg>
                <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">Average quality score per build run, oldest → newest.</p>
              </div>
            )}

            {/* Visual score over time (Art Director) — plotted next to the quality trend */}
            {vtrend.length >= 2 && (
              <div className="mt-1" data-testid="compiler-report-visualtrend">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Visual trend <span className="text-[#a855f7]">👁</span></div>
                  <div className="text-[11px] font-semibold" style={{ color: vtrendDelta >= 0 ? "#10b981" : "#f43f5e" }} data-testid="compiler-report-visualtrend-delta">
                    {vtrendDelta >= 0 ? `▲ +${vtrendDelta}` : `▼ ${vtrendDelta}`} over {vtrend.length} scans
                  </div>
                </div>
                <svg viewBox="0 0 300 64" className="w-full h-16 mt-1 rounded-lg bg-[hsl(var(--muted))]/30" preserveAspectRatio="none">
                  <line x1="0" y1="33" x2="300" y2="33" stroke="hsl(var(--border))" strokeWidth="0.5" strokeDasharray="3 3" />
                  <polyline points={vtrendPts} fill="none" stroke="url(#vtg)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                  {vtrend.map((p, i) => {
                    const x = vtrend.length > 1 ? (i / (vtrend.length - 1)) * 296 + 2 : 150;
                    const y = 60 - (Math.max(0, Math.min(100, p.score)) / 100) * 54;
                    return <circle key={i} cx={x} cy={y} r={i === vtrend.length - 1 ? 3.2 : 2} fill={p.score >= 80 ? "#10b981" : p.score >= 60 ? "#f59e0b" : "#f43f5e"} />;
                  })}
                  <defs><linearGradient id="vtg" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#a855f7" /><stop offset="1" stopColor="#ec4899" /></linearGradient></defs>
                </svg>
                <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">Art-Director visual score each time you scan, oldest → newest.</p>
              </div>
            )}

            {/* Per-system breakdown */}
            <div className="space-y-1.5 mt-1" data-testid="compiler-report-systems">
              <div className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">System scores</div>
              {systems.map((sys) => (
                <div key={sys.key} className="rounded-lg border border-[hsl(var(--border))] px-3 py-2" data-testid={`compiler-report-system-${sys.key}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium flex-1 truncate">{sys.label}</span>
                    {sys.fixed && <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-500"><CheckCircle2 className="w-3 h-3" /> fixed</span>}
                    <span className="text-sm font-extrabold tabular-nums" style={{ color: scoreColor(sys.score) }}>{sys.score}</span>
                  </div>
                  {sys.verdict && <p className="text-[11px] text-[hsl(var(--muted-foreground))] leading-snug mt-0.5">{sys.verdict}</p>}
                </div>
              ))}
            </div>

            {/* Persona findings */}
            {findings.length > 0 && (
              <div className="space-y-1.5 mt-2" data-testid="compiler-report-findings">
                <div className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">What the squad said</div>
                {findings.map((f, i) => (
                  <p key={i} className="text-[12px] text-[hsl(var(--muted-foreground))] leading-snug">
                    <b className="text-[hsl(var(--foreground))]">{f.persona}</b> <span className="opacity-60">on {f.system}:</span> {f.note}
                  </p>
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
